import React, { useCallback, useEffect, useMemo } from 'react';
import { Button, ModeEditTwoToneIcon } from 'tuui';
import { HelpTooltip, INDICATION_TYPES, IndicationContent, TextCheckbox } from 'taboola-ultimate-ui';
import { removeEmojis } from 'modules/codeless-events/utils/stringUtils';
import { GTM_EVENTS } from 'modules/taboola-common-frontend-modules/gtmTracker';
import { FormattedMessage } from 'modules/taboola-common-frontend-modules/i18n';
import { ConversionCategoryField } from 'modules/tracking/components/ConversionCategoryField/ConversionCategoryField';
import { TotalValueTooltip } from 'modules/tracking/components/TotalValueCheckBox/TotalValueTooltip/TotalValueTooltip';
import { CODELESS_CATEGORIES } from 'modules/tracking/consts/CodelessCategory';
import { STEP_IDS } from '../../config/stepIds';
import { useIncludeInTotalConversionHandler } from '../../hooks/useIncludeInTotalConversionHandler';
import { NewEventHeader } from '../Headers/NewEventHeader';
import { ConversionNameField } from './ConversionNameField';
import styles from './setupClicksEventCategory.module.scss';

export const SetupClicksEventCategory = ({
    currentConversion,
    navigateToStepId,
    isValueFeatureEnabled,
    updateDraftEvent,
    handleSaveDraftToPersistent,
    setFocusedConversionId,
}) => {
    const isFetchedEvent = currentConversion?.eventName?.includes('tce');

    useEffect(() => {
        if (
            !isFetchedEvent &&
            currentConversion &&
            currentConversion.category &&
            currentConversion.trackedElements &&
            currentConversion.trackedElements.length > 0
        ) {
            const firstTrackedElement = currentConversion.trackedElements[0];
            if (!firstTrackedElement.displayText) {
                updateDraftEvent(draft => ({
                    ...draft,
                    trackedElements: currentConversion.trackedElements,
                }));
            }
        }
    }, [currentConversion, isFetchedEvent, updateDraftEvent]);

    const buttonName = useMemo(() => {
        const displayText = currentConversion?.trackedElements[0].displayText;

        if (isFetchedEvent || displayText) {
            return displayText;
        }

        return currentConversion?.category;
    }, [currentConversion, isFetchedEvent]);

    const conversionName = currentConversion?.conversionName;
    const handleConversionNameChange = useCallback(
        value => {
            updateDraftEvent(draft => ({
                ...draft,
                conversionName: removeEmojis(value),
            }));
        },
        [updateDraftEvent]
    );

    const includeInTotalConversions = currentConversion?.includeInTotalConversions;
    const { handleSelectCheckbox, handleUnselectCheckbox } = useIncludeInTotalConversionHandler(updateDraftEvent);

    const handleCategoryChange = useCallback(
        categoryValue => {
            updateDraftEvent(draft => ({
                ...draft,
                category: categoryValue.value,
            }));
        },
        [updateDraftEvent]
    );

    const handleNextInternal = () => {
        if (isValueFeatureEnabled) {
            navigateToStepId(STEP_IDS.SETUP_VALUE);
            return;
        }

        handleSaveDraftToPersistent();
        setFocusedConversionId(undefined);
    };

    const handleBackInternal = () => {
        if (!navigateToStepId) {
            return;
        }

        navigateToStepId(STEP_IDS.BUTTON_ANALYZER_STEP);
    };

    return (
        <div className={styles['container']}>
            <NewEventHeader handlerArrowLeftButton={handleBackInternal} />
            <div className={styles['title-and-selector-container']}>
                <IndicationContent containerClassName={styles['container-indication']} type={INDICATION_TYPES.SUCCESS}>
                    <FormattedMessage
                        id="codeless.setup.clicks.event.category.indication"
                        defaultMessage={`<strong>‘${buttonName}</strong>' Button was selected`}
                        values={{ value: buttonName }}
                    />
                    <div className={styles['right-icon']} onClick={handleBackInternal}>
                        <ModeEditTwoToneIcon />
                    </div>
                </IndicationContent>

                <div className={styles['setup-title']}>
                    <FormattedMessage
                        id="codeless..setup.clicks.event.category.header"
                        defaultMessage="Setup button clicks event"
                    />
                </div>
                <ConversionNameField
                    conversionName={conversionName}
                    buttonName={buttonName}
                    onConversionNameChange={handleConversionNameChange}
                />
                <ConversionCategoryField
                    field="codelessDraftEvent.category"
                    isCodeless={true}
                    onChange={handleCategoryChange}
                    skipValidations={false}
                    categories={CODELESS_CATEGORIES}
                    disabled={isFetchedEvent}
                />
            </div>
            <div className={styles['checkbox-container']}>
                <TextCheckbox
                    labelClassName={styles['checkbox-label']}
                    onSelectCheckbox={handleSelectCheckbox}
                    onUnselectCheckbox={handleUnselectCheckbox}
                    selectedValues={[includeInTotalConversions]}
                    independent
                    value
                    title={
                        <FormattedMessage
                            id="tracking.conversion.properties.total.conversion.checkbox.title"
                            defaultMessage="Include in total conversions"
                        />
                    }
                    data-metrics-component="Total value checkbox"
                    data-metrics-event-name={GTM_EVENTS.USABILITY}
                    data-metrics-value={includeInTotalConversions}
                />
                <HelpTooltip>
                    <TotalValueTooltip />
                </HelpTooltip>
            </div>

            <footer className={styles['footer']}>
                <Button
                    onClick={handleBackInternal}
                    variant={Button.variant.outline}
                    size={Button.size.md}
                    aria-label="Cancel"
                >
                    <FormattedMessage id="codeless.cancel.button" defaultMessage="Cancel" />
                </Button>
                <Button
                    onClick={handleNextInternal}
                    variant={Button.variant.primary}
                    size={Button.size.md}
                    className={styles['next-button']}
                    aria-label="Next"
                    disabled={!currentConversion?.category || !currentConversion?.conversionName}
                >
                    {isValueFeatureEnabled ? (
                        <FormattedMessage id="codeless.next.button" defaultMessage="Next" />
                    ) : (
                        <FormattedMessage id="codeless.create.event.button" defaultMessage="Create Event" />
                    )}
                </Button>
            </footer>
        </div>
    );
};
